import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useHistory } from "react-router";

import {
  Alert,
  Button,
  Icon,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import {
  useGetAllRelationships,
  useGetCashWithdrawable,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import { createCurrencyCloudTransfer } from "~/v2/api/rest/currencyCloud";
import { FullScreenSpinner } from "~/v2/components/common";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { FAQ } from "../../../../../components/common/FAQ";
import { Layout } from "../../common/Layout";
import { postWithdrawSurvey } from "../../common/util";
import { CURRENCYCLOUD_WITHDRAW_FAQ } from "../../link/currencyCloud";
import { FromTo, type FromToItem } from "../../transferForm/FromTo";
import { ReasonFields } from "../../transferForm/ReasonFields";
import {
  getCorrectAmount,
  type TransferSchema,
  withdrawableSuperRefinement,
  zTransferSchema,
} from "../../transferForm/schema";
import { TransferFormButtons } from "../../transferForm/TransferFormButtons";
import { TransferFormFields } from "../../transferForm/TransferFormFields";

const CurrencyCloudWithdrawalPage = () => {
  const history = useHistory();

  const [isSubmitChecked, setIsSubmitChecked] = useState(false);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(true);

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const { currencyCloud } = useGetAllRelationships({
    account: currentAccount,
    countryOfTaxResidence: details?.country_of_tax_residence || "",
  });

  const hasCCRelationship = Boolean(currencyCloud.account);

  const { data: withdrawableData } = useGetCashWithdrawable({
    accountID: currentAccount?.id || "",
    options: {
      enabled: !!currentAccount?.id,
    },
  });
  const withdrawable = withdrawableData ?? 0;

  const form = useForm<TransferSchema>({
    resolver: zodResolver(
      zTransferSchema.superRefine((fields, refinementContext) =>
        withdrawableSuperRefinement({
          fields,
          refinementContext,
          withdrawable,
        }),
      ),
    ),
  });

  const amountString = form.watch("amount");
  const amount = getCorrectAmount(amountString);
  const isConfirmDisabled = amount <= 0 || amount > withdrawable;

  const reason = form.watch("reason");
  const reasonDetails = form.watch("reason_details");

  const alpacaAccountInfo: FromToItem = {
    imgSrc: "resources/images/logo-collapsed.svg",
    name: "Alpaca Account",
    account: currentAccount?.account_number ?? "",
  };

  const onBackClicked = () => {
    history.push(Path.ROUTE_FUNDING_WITHDRAW);
  };

  const withdraw = useMutationRequest(
    (amount: number) => {
      setIsSubmitEnabled(false);
      return createCurrencyCloudTransfer(currentAccount?.id || "", {
        amount: amount.toString(),
      });
    },
    {
      onSuccess: () => {
        toastSuccess("Transfer created");

        if (reason) {
          postWithdrawSurvey(currentAccount?.id ?? "", {
            reason,
            reasonDetails,
          });
        }

        history.push(Path.ROUTE_FUNDING_HISTORY);
      },
      onSettled: () => {
        setIsSubmitEnabled(true);
      },
    },
  );

  const onSubmitValid: SubmitHandler<TransferSchema> = ({ amount }) => {
    withdraw.mutate(getCorrectAmount(amount));
  };

  const onSubmitInvalid = () => {
    toastError("Please check your withdrawal details");
  };

  if (!currentAccount || !details || !hasCCRelationship) {
    return <FullScreenSpinner />;
  }

  return (
    <Layout
      title="Withdraw Funds via CurrencyCloud"
      subtitle="Withdraw funds from your Alpaca account using CurrencyCloud connection."
      className="p-2"
      faq={<FAQ faq={CURRENCYCLOUD_WITHDRAW_FAQ} />}
    >
      <Button variant="secondary" onClick={onBackClicked} className="mb-8">
        <Icon name="ArrowLeftCircle" className="mr-2" />
        Back
      </Button>
      <div className="w-full max-w-lg">
        <Text>
          Alpaca has partnered with CurrencyCloud to facilitate withdrawing your
          funds faster and cheaper than traditional wire.
        </Text>
        <Text className="py-4">
          Alpaca charges a 1.5% transaction (maximum $40) fee on withdrawals.
          Minimum withdrawal equivalent to $2.00.
        </Text>
        <Alert
          variant="info"
          message="Your funds will be sent to your local bank account in your local currency."
          className="mb-8"
        />
        <form
          onSubmit={form.handleSubmit(onSubmitValid, onSubmitInvalid)}
          className="rounded border border-gray-200 p-4"
        >
          <FromTo
            from={alpacaAccountInfo}
            to={{
              name: currencyCloud.account?.bank_name ?? "",
              account: currencyCloud.account?.account_number ?? "",
            }}
            className="mb-8"
          />
          <TransferFormFields
            form={form}
            isInputDisabled={isSubmitChecked}
            withdrawableAmount={withdrawable}
          />
          <ReasonFields form={form} withdrawableAmount={withdrawable} />
          <TransferFormButtons
            direction="withdrawal"
            isSubmitChecked={isSubmitChecked}
            setIsSubmitChecked={setIsSubmitChecked}
            isSubmitEnabled={isSubmitEnabled}
            isConfirmDisabled={isConfirmDisabled}
            amount={amountString}
          />
        </form>
      </div>
    </Layout>
  );
};

export default CurrencyCloudWithdrawalPage;
